import React, { useEffect, useState } from 'react';
import { Button, LabelInput } from 'modules/v2/common/AtomicDesign/atoms';
import { Tooltip } from 'flowbite-react';
import { QuestionCircleIcon, RestartIcon, TrashIcon } from 'modules/v2/common/components/SvgIcon';

type FileUploadProps = {
  label: string;
  name: string;
  onChange: (file: File) => void;
  accept?: string;
  acceptLabel?: string;
  tooltip?: string;
  subLabel?: string;
  required?: boolean;
  value?: File | string | null;
};

export const TemplateFilePicker: React.FC<FileUploadProps> = ({
  label,
  name,
  accept = '.png,.jpg,.jpeg',
  acceptLabel = '',
  subLabel = '',
  tooltip = '',
  required = false,
  value,
  onChange,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | string>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      onChange(file);
    }
  };

  const handleChangePhoto = () => {
    const fileInput = document.getElementById(name) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleRemovePhoto = () => {
    setUploadedFile(null);
    onChange(null);
  };

  useEffect(() => setUploadedFile(value), [value]);

  return (
    <div className="flex-1 max-h-[200px]">
      <div className="flex">
        <LabelInput className="mr-1.5" required={required}>
          {label}
        </LabelInput>
        {tooltip && (
          <Tooltip theme={{ style: { dark: 'bg-[#252525] text-white' }, }} content={tooltip}>
            <QuestionCircleIcon />
          </Tooltip>
        )}
      </div>
      {subLabel && <span className="flex text-neutral-500 text-sm leading-5 font-medium mb-5">{subLabel}</span>}
      <div className="group bg-neutral-50 border rounded-md border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-200 transition duration-300 ease-in-out p-6 text-center">
        <input
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
          id={name}
        />
        {uploadedFile ? (
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img
              alt="uploaded"
              height="200px"
              className="group-hover:brightness-[90%] transition duration-300 ease-in-out object-contain w-full max-h-[120px]"
              src={typeof uploadedFile === 'string' ? uploadedFile : URL.createObjectURL(uploadedFile)}
            />
            {isHovered && (
              <div className="absolute inset-0 flex items-center justify-center gap-3">
                <Button
                  type="outlined"
                  onClick={handleChangePhoto}
                >
                  <RestartIcon />
                  <span className="ml-2">Change Photo</span>
                </Button>
                <Button
                  type="outlined"
                  onClick={handleRemovePhoto}
                >
                  <TrashIcon fill="#000" />
                  <span className="ml-2">Remove</span>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="text-cyan-500 text-sm font-semibold mb-2">
              Browse for photo <span className="text-[#252525]">or drag and drop</span>
            </div>
            <div className="text-gray-400 text-xs mb-4">
              {acceptLabel || '.png, .jpg, or .gif only'}
            </div>
            <label
              htmlFor={name}
              className="bg-[#252525] font-semibold text-white px-4 py-2 rounded cursor-pointer hover:bg-gray-700"
            >
              Browse
            </label>
          </>
        )}
        {/* {uploadedFile && typeof uploadedFile !== 'string' && (
          <div className="mt-2 text-sm text-green-600">
            File selected: {uploadedFile.name}
          </div>
        )} */}
      </div>
    </div>
  );
};
